// review-report-merge.ts — H1 nửa B (plan 20260915-1650, P3 câu hỏi treo 2).
//
// Gộp verdict của HAI reviewer thành một `WorkerReviewReport` để gate push đọc.
//
// Vì sao cần: P3 dựng reviewer thứ hai (T-9002, `review-clean-v1`, context
// sạch) và BẮT BUỘC nó khai `review_report` (nửa A), nhưng verdict đó không ai
// đọc — `review-report-promotion-wiring.ts` chỉ promote `review-local-v1`, và
// `<controlRoot>/review-staging/<sha>.json` khoá theo SHA nên chỉ chứa được một
// report. Kết quả: một phiên Claude thật chạy, tốn tiền, rồi verdict rơi vào hư
// không. Đúng lớp lỗi P20 — cơ chế tồn tại, không ai gọi, tạo cảm giác an toàn
// giả, tệ hơn là không có.
//
// LUẬT GỘP — user chốt 2026-09-16: **risk cao nhất thắng**.
// Chiều ngược lại ("cả hai phải cao mới chặn") nghĩa là một reviewer thấy
// `critical` vẫn để push đi — không ai chọn thế sau khi nói thành lời.
//
// Gộp ở tầng ĐỌC (`run-command-release-flow-wiring.ts`), không phải tầng ghi:
// mỗi reviewer promote ra path riêng nên vẫn truy được AI nói gì, và mission chỉ
// có một reviewer (mọi mission trước thay đổi này + mọi fixture e2e) đi đúng
// đường cũ, không qua nhánh gộp nào.
import type { WorkerReviewReport } from "./review-local-adapter.js";

/**
 * Thứ tự rủi ro. Định nghĩa lại ở đây thay vì import: `RISK_RANK` trong
 * `review-local-adapter.ts` là module-private và nó gác một câu hỏi KHÁC
 * ("report này có đủ sạch để ghi receipt không"). Hai bảng cùng giá trị nhưng
 * khác mục đích — gộp lại thì sau này ai sửa ngưỡng chặn push sẽ vô tình đổi cả
 * luật gộp.
 */
const RISK_ORDER: Record<string, number> = { low: 0, medium: 1, high: 2, critical: 3 };

/**
 * Risk nào "nặng" hơn. Giá trị NGOÀI enum được coi là nặng nhất có thể:
 * `isValidRisk` của adapter sẽ chặn push trên giá trị lạ, nên giữ nguyên nó là
 * fail-closed; rơi về "low" sẽ tự mở cửa bằng một chuỗi không ai hiểu.
 */
function riskRank(risk: unknown): number {
  if (typeof risk !== "string") return Number.MAX_SAFE_INTEGER;
  const rank = RISK_ORDER[risk];
  return rank === undefined ? Number.MAX_SAFE_INTEGER : rank;
}

/**
 * `secret_scan` của report nào phải thắng.
 *
 * Fail-closed theo đúng hai điều kiện `controllerRecordReviewReceiptFromReport`
 * đang chặn (`review-local-adapter.ts:60,72`):
 *  - THIẾU `secret_scan` thắng tuyệt đối — nếu lấy `secret_scan` của reviewer
 *    kia để lấp vào, một reviewer im lặng sẽ mở được cửa mà chính nó không
 *    chứng minh được gì;
 *  - còn lại thì `findings` cao hơn thắng: một reviewer thấy secret là đủ để
 *    người phải đọc.
 */
function pickSecretScan(reports: readonly WorkerReviewReport[]): WorkerReviewReport["secret_scan"] {
  if (reports.some((report) => report.secret_scan === undefined)) return undefined;
  let worst = reports[0]?.secret_scan;
  for (const report of reports) {
    const scan = report.secret_scan;
    if (scan === undefined) return undefined;
    if (worst === undefined || scan.findings > worst.findings) worst = scan;
  }
  return worst;
}

/** Hợp nhất evidence hash, giữ thứ tự xuất hiện, khử trùng. */
function unionEvidenceHashes(reports: readonly WorkerReviewReport[]): string[] | undefined {
  const hashes = reports.flatMap((report) => report.evidence_hashes ?? []);
  return hashes.length > 0 ? [...new Set(hashes)] : undefined;
}

/**
 * Gộp 0..n verdict thành một report cho gate push.
 *
 *  - `[]` ⇒ `undefined`: `resumeReleaseFlowIfConfigured` trả
 *    `waiting_review_report` (supervisor-release-flow-resume.ts:121) — KHÔNG
 *    bao giờ bịa ra một risk value, cùng nguyên tắc `review-report-promotion.ts`
 *    đã ghi cho ca report hỏng.
 *  - đúng MỘT report ⇒ trả nguyên văn, không đụng gì. Đây là đường của mọi
 *    mission trước thay đổi này; giữ nó nguyên vẹn quan trọng hơn mọi thứ khác
 *    trong file.
 */
export function mergeReviewReportVerdicts(
  reports: readonly WorkerReviewReport[],
): WorkerReviewReport | undefined {
  if (reports.length === 0) return undefined;
  if (reports.length === 1) return reports[0];

  // Seed tường minh (M1, code-reviewer 260916 phase-07): `reduce` không seed
  // chỉ an toàn nhờ hai guard `length === 0/1` ở trên — một phụ thuộc NGẦM.
  // Ai gỡ guard sau này sẽ nhận TypeError trên mảng rỗng thay vì `undefined`.
  const riskiest = reports.reduce(
    (worst, report) => (riskRank(report.risk) > riskRank(worst.risk) ? report : worst),
    reports[0] as WorkerReviewReport,
  );
  const secretScan = pickSecretScan(reports);
  const evidenceHashes = unionEvidenceHashes(reports);

  return {
    risk: riskiest.risk,
    ...(secretScan !== undefined ? { secret_scan: secretScan } : {}),
    ...(evidenceHashes !== undefined ? { evidence_hashes: evidenceHashes } : {}),
  };
}
